/**
 * O resumo do evento em PDF, para a organização levar.
 *
 * Desenhado com jsPDF, que já é dependência do projeto (o crachá A4 usa). Não entra
 * biblioteca nova para isso: um relatório de três tabelas não justifica somar peso ao
 * bundle nem risco a um sistema que está no ar.
 *
 * As mesmas ressalvas da tela vão impressas no PDF. Um número solto num papel circula sem
 * quem o explique — "pessoas" e "leituras" precisam vir com o que significam, senão a soma
 * dos dias vira "participantes do evento" na mão de quem só recebeu o arquivo.
 */

export interface DiaAcessoPdf {
  dia: string
  pessoas: number
  leituras: number
  salas: number
}

export interface DiaCheckinPdf {
  dia: string
  pessoas: number
  retiradas: number
}

export interface OrigemPdf {
  estados: Array<{ uf: string; nome: string; total: number }>
  nacionalidades: Array<{ valor: string; total: number }>
  semEstado: number
  totalPessoas: number
}

const MARGEM = 15
const LARGURA = 210 // A4 retrato, em mm
const RODAPE = 282

const diaLegivel = (ymd: string) => {
  const [a, m, d] = ymd.split('-')
  if (!a || !m || !d) return ymd
  const data = new Date(Number(a), Number(m) - 1, Number(d))
  const semana = data.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '')
  return `${d}/${m}/${a} (${semana})`
}

export async function exportarResumoPdf(dados: {
  acessos: DiaAcessoPdf[]
  checkins: DiaCheckinPdf[]
  origem: OrigemPdf | null
  nomeEvento?: string
}): Promise<void> {
  const { default: jsPDF } = await import('jspdf')
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })

  let y = MARGEM

  /** Quebra de página quando o conteúdo não cabe, mantendo a margem inferior. */
  const espaco = (altura: number) => {
    if (y + altura > RODAPE) {
      doc.addPage()
      y = MARGEM
    }
  }

  const titulo = (texto: string, tamanho = 11) => {
    espaco(12)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(tamanho)
    doc.setTextColor(20, 20, 20)
    doc.text(texto, MARGEM, y)
    y += tamanho * 0.5
  }

  const legenda = (texto: string) => {
    espaco(8)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8)
    doc.setTextColor(110, 110, 110)
    // `splitTextToSize` quebra em várias linhas: uma observação longa escapava da folha
    // pela direita e sumia.
    const linhas = doc.splitTextToSize(texto, LARGURA - MARGEM * 2) as string[]
    for (const l of linhas) {
      espaco(5)
      doc.text(l, MARGEM, y)
      y += 4
    }
    y += 1
  }

  /** Tabela simples, com cabeçalho repetido quando a página vira. */
  const tabela = (
    cabecalho: string[],
    linhas: Array<Array<string | number>>,
    larguras: number[],
    destaque?: (i: number) => boolean,
  ) => {
    const alturaLinha = 6

    const desenharCabecalho = () => {
      espaco(alturaLinha + 2)
      doc.setFillColor(238, 240, 243)
      doc.rect(MARGEM, y - 4, LARGURA - MARGEM * 2, alturaLinha, 'F')
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(8.5)
      doc.setTextColor(60, 60, 60)
      let x = MARGEM + 2
      cabecalho.forEach((c, i) => {
        const alinharDireita = i > 0
        doc.text(c, alinharDireita ? x + larguras[i] - 4 : x, y, {
          align: alinharDireita ? 'right' : 'left',
        })
        x += larguras[i]
      })
      y += alturaLinha
    }

    desenharCabecalho()
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)

    linhas.forEach((linha, i) => {
      if (y + alturaLinha > RODAPE) {
        doc.addPage()
        y = MARGEM
        desenharCabecalho()
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(9)
      }
      if (destaque?.(i)) {
        doc.setFillColor(232, 245, 243)
        doc.rect(MARGEM, y - 4, LARGURA - MARGEM * 2, alturaLinha, 'F')
      }
      doc.setTextColor(30, 30, 30)
      let x = MARGEM + 2
      linha.forEach((celula, j) => {
        const alinharDireita = j > 0
        doc.text(String(celula), alinharDireita ? x + larguras[j] - 4 : x, y, {
          align: alinharDireita ? 'right' : 'left',
        })
        x += larguras[j]
      })
      y += alturaLinha
    })
    y += 3
  }

  // ── cabeçalho do documento ────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(15)
  doc.setTextColor(20, 20, 20)
  doc.text('Resumo do evento', MARGEM, y)
  y += 7

  if (dados.nomeEvento) {
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(10)
    doc.setTextColor(80, 80, 80)
    const linhas = doc.splitTextToSize(dados.nomeEvento, LARGURA - MARGEM * 2) as string[]
    for (const l of linhas) {
      doc.text(l, MARGEM, y)
      y += 5
    }
  }

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  doc.setTextColor(120, 120, 120)
  doc.text(
    `Gerado em ${new Date().toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}`,
    MARGEM,
    y,
  )
  y += 8

  // ── 1. público nas palestras ──────────────────────────────────────────────
  titulo('Público nas palestras, por dia')
  if (dados.acessos.length === 0) {
    legenda('Nenhum acesso registrado.')
  } else {
    tabela(
      ['Dia', 'Pessoas', 'Leituras', 'Salas'],
      dados.acessos.map(d => [diaLegivel(d.dia), d.pessoas, d.leituras, d.salas]),
      [80, 35, 35, 30],
    )
    legenda(
      'Pessoas: participantes distintos que entraram em alguma sala naquele dia. ' +
        'Leituras: total de passagens pelo leitor — a mesma pessoa pode ler o crachá ' +
        'várias vezes no dia. A soma dos dias NÃO é o público do evento: quem foi em mais ' +
        'de um dia conta em cada um.',
    )
  }

  // ── 2. credenciamento ─────────────────────────────────────────────────────
  titulo('Credenciamento (check-in), por dia')
  if (dados.checkins.length === 0) {
    legenda('Nenhum check-in registrado.')
  } else {
    tabela(
      ['Dia', 'Pessoas', 'Impressão sem checkin'],
      dados.checkins.map(d => [diaLegivel(d.dia), d.pessoas, d.retiradas]),
      [80, 45, 55],
    )
    legenda(
      'Pessoas: quantas retiraram credencial naquele dia. A segunda coluna conta todas ' +
        'as emissões, inclusive reimpressão da mesma pessoa — por isso é maior.',
    )
  }

  // ── 3. origem ─────────────────────────────────────────────────────────────
  if (dados.origem) {
    const o = dados.origem
    titulo(`De onde vem o público — ${o.estados.length} estado(s)`)
    tabela(
      ['Estado', 'UF', 'Inscritos'],
      o.estados.map(e => [e.nome || e.uf, e.uf, e.total]),
      [95, 30, 55],
    )
    if (o.semEstado > 0) {
      legenda(`${o.semEstado} pessoa(s) sem estado informado, de ${o.totalPessoas} no total.`)
    }

    titulo('Nacionalidade declarada', 10)
    tabela(
      ['Nacionalidade', 'Pessoas'],
      o.nacionalidades.map(n => [n.valor, n.total]),
      [125, 55],
    )
    legenda(
      'O país de origem não é perguntado na inscrição — só se a pessoa é brasileira ou ' +
        'estrangeira. Por isso não há contagem de países.',
    )
  }

  // ── rodapé em todas as páginas ────────────────────────────────────────────
  const paginas = doc.getNumberOfPages()
  for (let p = 1; p <= paginas; p++) {
    doc.setPage(p)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(7.5)
    doc.setTextColor(150, 150, 150)
    doc.text(`Página ${p} de ${paginas}`, LARGURA - MARGEM, 290, { align: 'right' })
    doc.text('Sistema de Credenciamento — Quantic Technology', MARGEM, 290)
  }

  const hoje = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date())
  doc.save(`resumo-do-evento-${hoje}.pdf`)
}
